import { makeStyles, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import authAPI, { ConsentType } from '../../api/auth';
import { TestId } from '../../components/Testing/TestId';
import {
  Button,
  Consent,
  PageLoader,
  SnackMessage,
  StickyContent,
  Text,
} from '../../components/common';
import { ConsentCreateModal } from '../../components/common/consent-create-modal';
import { ConsentEditModal } from '../../components/common/consent-edit-modal';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';

type ConsentTypeFilter = 'Mentor' | 'Founder';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  emptyContainer: {
    minHeight: '100%',
  },
  title: {
    margin: '0 0 8px',
  },
  droppableArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    marginTop: 20,
  },
  actionsBlock: {
    marginTop: 56,
    '&> div': {
      display: 'flex',
      gap: 16,
    },
  },
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  emptyBlock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 696,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: 500,
  },
  emptyStateBtn: {
    marginTop: 16,
  },
});

interface TenantConsentsPageProps {
  filterType?: ConsentTypeFilter;
  pageTitle?: string;
  pageDescription?: string;
}

function TenantConsentsPage({
  filterType,
  pageTitle,
  pageDescription,
}: TenantConsentsPageProps) {
  const classes = useStyles();
  const { user, hasAccessToAction } = useContext(UserContext);
  const { rb } = useResourceBundles();
  const [updatingConsentsIds, setUpdaingConsentsIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [deletingConsentsIds, setDeletingConsentsIds] = useState<{
    [x: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [consents, setConsents] = useState<ConsentType[]>([]);
  const [isReordering, setIsReordering] = useState(false);
  const [editingConsent, setEditingConsent] = useState<ConsentType | null>(
    null,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const title = useMemo(() => {
    if (pageTitle) return pageTitle;
    if (filterType === 'Mentor') return `${rb('mentor-u')} Commitments`;
    if (filterType === 'Founder') return 'Founder Commitments';
    return 'Commitments';
  }, [filterType, pageTitle, rb]);

  const description = useMemo(() => {
    if (pageDescription) return pageDescription;
    if (filterType === 'Mentor')
      return `Here you can create and modify commitments for ${rb('mentors')}.`;
    if (filterType === 'Founder')
      return 'Here you can create and modify commitments for founders.';
    return `Here you can create and modify commitments for founders and ${rb(
      'mentors',
    )}.`;
  }, [filterType, pageDescription, rb]);

  const showErrorSnackbar = useCallback(
    (message: string, errorMessage?: string) => {
      enqueueSnackbar(message, {
        content: (key, msg) =>
          SnackMessage({
            key,
            message: msg,
            variant: 'error',
            additionalMessage: errorMessage,
          }),
        variant: 'error',
      });
    },
    [enqueueSnackbar],
  );

  const normalizeConsents = useCallback((items: ConsentType[]) => {
    return [...items]
      .map((item, idx) => ({
        ...item,
        orderNo:
          item.orderNo === 0 || item.orderNo
            ? item.orderNo
            : Number.MAX_SAFE_INTEGER - (items.length - idx),
      }))
      .sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0))
      .map((item, index) => ({ ...item, orderNo: index }));
  }, []);

  const applyFilter = useCallback(
    (items: ConsentType[]) =>
      filterType ? items.filter((item) => item.type === filterType) : items,
    [filterType],
  );

  const persistOrderBulk = useCallback(
    async (orderedConsents: ConsentType[], prevConsents: ConsentType[]) => {
      try {
        setIsReordering(true);
        await authAPI.updateConsentsBulk(orderedConsents);
        setIsReordering(false);
      } catch (e: any) {
        setConsents(prevConsents);
        setIsReordering(false);
        throw e;
      }
    },
    [],
  );

  const handleDragEnd = useCallback(
    async ({ destination, source }: DropResult) => {
      if (!destination || destination.index === source.index) {
        return;
      }
      const prevConsents = consents;
      const reordered = [...consents];
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      const orderedConsents = reordered.map((item, index) => ({
        ...item,
        orderNo: index,
        tenantId: item.tenantId || user?.id || '',
      }));
      setConsents(orderedConsents);
      try {
        await persistOrderBulk(orderedConsents, prevConsents);
        enqueueSnackbar(
          'The commitments order update request was successfully sent',
          {
            variant: 'success',
          },
        );
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        showErrorSnackbar(
          'An error occurred while sending commitments order update request. Please, try again.',
          messageError,
        );
      }
    },
    [consents, persistOrderBulk, enqueueSnackbar, user, showErrorSnackbar],
  );

  const handleCreateConsent = useCallback(
    async (value, callback) => {
      try {
        setIsCreating(true);
        if (user) {
          const createdConsent = (await authAPI.createConsent({
            tenantId: user?.id,
            type: filterType || value.type,
            body: value.body,
            orderNo: consents.length,
          })) as ConsentType;
          setConsents((prev) => [...prev, createdConsent]);
        }
        setIsCreating(false);
        callback();
        enqueueSnackbar('The commitment was successfully added.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        showErrorSnackbar(
          'An error occurred while creating the commitment. Please, try again.',
          messageError,
        );
        setIsCreating(false);
      }
    },
    [enqueueSnackbar, user, consents, showErrorSnackbar, filterType],
  );

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  const handleOpenCreateModal = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleConsentEdit = useCallback((consent: ConsentType) => {
    setEditingConsent(consent);
  }, []);

  const handleOpenEditModal = useCallback(
    (consent: ConsentType) => () => {
      handleConsentEdit(consent);
    },
    [handleConsentEdit],
  );

  const handleConsentUpdate = useCallback(
    async (value, callback) => {
      try {
        setUpdaingConsentsIds((prev) => ({ ...prev, [value.id]: true }));
        if (user) {
          await authAPI.updateConsent({
            tenantId: user?.id,
            ...value,
          } as ConsentType);
          setConsents((prev) =>
            prev.map((consentsList, index) =>
              consentsList.id === value.id
                ? { ...value, orderNo: consentsList.orderNo ?? index }
                : consentsList,
            ),
          );
        }
        setUpdaingConsentsIds((prev) => ({ ...prev, [value.id]: false }));
        callback();
        enqueueSnackbar('The commitment was successfully updated.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        showErrorSnackbar(
          'An error occurred while updating the commitment. Please, try again.',
          messageError,
        );
        setUpdaingConsentsIds((prev) => ({ ...prev, [value.id]: false }));
      }
    },
    [user, enqueueSnackbar, showErrorSnackbar],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditingConsent(null);
  }, []);

  const handleConsentRemove = useCallback(
    async (value, callback: () => any) => {
      try {
        setDeletingConsentsIds((prev) => ({ ...prev, [value.id]: true }));
        if (user) {
          await authAPI.deleteConsent(value.id);
          const prevConsents = consents;
          const filtered = consents.filter(
            (mentorConsent) => mentorConsent.id !== value.id,
          );
          const normalized = normalizeConsents(filtered);
          setConsents(normalized);
          try {
            await persistOrderBulk(normalized, prevConsents);
          } catch (orderError) {
            // Order update failed, but deletion was successful
            // Don't show notification, just log the error
            console.error('Failed to update order after deletion:', orderError);
          }
        }
        callback();
        setDeletingConsentsIds((prev) => ({ ...prev, [value.id]: false }));
        enqueueSnackbar('The commitment was successfully removed.', {
          variant: 'success',
        });
      } catch (e: any) {
        const messageError = e.response?.data?.message;

        showErrorSnackbar(
          'An error occurred while removing the commitment. Please, try again.',
          messageError,
        );
        setDeletingConsentsIds((prev) => ({ ...prev, [value.id]: false }));
      }
    },
    [
      enqueueSnackbar,
      normalizeConsents,
      persistOrderBulk,
      user,
      consents,
      showErrorSnackbar,
    ],
  );

  const loadConsents = useCallback(async () => {
    try {
      setIsLoading(true);
      const responseConsents = await authAPI.getConsents();
      setConsents(normalizeConsents(applyFilter(responseConsents)));
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  }, [applyFilter, normalizeConsents]);

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div
      className={consents.length ? classes.container : classes.emptyContainer}>
      <Typography variant='h3' className={classes.title}>
        {title}
      </Typography>
      <Text variant='normal'>{description}</Text>
      {!consents.length ? (
        <div className={classes.emptyBlock}>
          <div className={classes.emptyState}>
            <Text variant='normal' testid='consents-list-no-items'>
              This panel contains a list of commitments.
              <br />
              {hasAccessToAction('consents.update')
                ? 'We were not able to find information you requested, but feel free to add something new!'
                : ''}
            </Text>
            {hasAccessToAction('consents.update') && (
              <TestId testId='add-button'>
                <Button
                  className={classes.emptyStateBtn}
                  startIcon={<AddIcon />}
                  variant='contained'
                  onClick={handleOpenCreateModal}
                  color='primary'>
                  Add Commitment
                </Button>
              </TestId>
            )}
          </div>
        </div>
      ) : (
        <>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='consents-list'>
              {(provided) => (
                <div
                  className={classes.droppableArea}
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {consents.map((consent, index) => (
                    <Draggable
                      key={consent.id}
                      draggableId={consent.id}
                      index={index}
                      isDragDisabled={!hasAccessToAction('consents.update')}>
                      {(draggableProvided, snapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}>
                          <Consent
                            consent={consent}
                            updating={updatingConsentsIds[consent.id]}
                            deleting={deletingConsentsIds[consent.id]}
                            onEdit={handleOpenEditModal(consent)}
                            onRemove={handleConsentRemove}
                            readOnly={!hasAccessToAction('consents.update')}
                            index={index}
                            dragHandleProps={draggableProvided.dragHandleProps}
                            dragging={snapshot.isDragging}
                            dragLoading={isReordering}
                            showType={!filterType}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {hasAccessToAction('consents.update') && (
            <div className={classes.actionsBlock}>
              <StickyContent>
                <TestId testId='add-button'>
                  <Button
                    onClick={handleOpenCreateModal}
                    disabled={isCreating}
                    variant='contained'
                    startIcon={<AddIcon />}>
                    Add Commitment
                  </Button>
                </TestId>
              </StickyContent>
            </div>
          )}
        </>
      )}
      <ConsentCreateModal
        open={showCreateModal}
        loading={isCreating}
        fixedType={filterType}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateConsent}
      />
      <ConsentEditModal
        open={!!editingConsent}
        consent={editingConsent}
        loading={
          editingConsent ? updatingConsentsIds[editingConsent.id] : false
        }
        fixedType={filterType}
        onClose={handleCloseEditModal}
        onSubmit={handleConsentUpdate}
      />
    </div>
  );
}

export default TenantConsentsPage;
